import axios from '@/plugins/axios'
import customErrors from '@/store/error'
import convertObjToArray from '@/helpers/convertObjToArray'
import {createItem, deleteItem, editItem, getItemById, getItems} from '@/store/main'
import {currencySymbols} from '@/helpers/variables/currencies'
import {DEFAULT_PARAMS, DEFAULT_SIZE} from '@/helpers/table'
import {addFilter, createParams, isDuplicateFilter, updateFilter} from '@/helpers/filterHelper'

const TYPE_LOADING_USER = 'USER'
const TYPE_LOADING_USER_ACTION = 'ACTION'

const state = () => ({
  items: [],
  item: null,
  loading: false,
  actionLoading: false,
  dataPagination: {},
  userIdItems: [],
  userNameItems: [],
  params: {...DEFAULT_PARAMS},
  isShowFilterBar: false,
  selectedAppFilter: {},
  selectedTableFilters: [],
  isResetSortParams: false,
})

const getters = {
  permission: (state, getters, rootState) => {
    return {
      read: rootState.auth?.permissions?.some((a) => a === 'CLIENT_READ'),
      readAll: rootState.auth?.permissions?.some((a) => a === 'CLIENT_READ_ALL'),
      new: rootState.auth.permissions?.some((a) => a === 'CLIENT_CREATE'),
      update: rootState.auth?.permissions?.some((a) => a === 'CLIENT_UPDATE'),
      updatePayment: rootState.auth?.permissions?.some((a) => a === 'CLIENT_PAYMENT_INFO_UPDATE'),
      changeInn: rootState.auth?.permissions?.some(
        (a) => a === 'CLIENT_INN_MEDIATOR_AND_MEDIATOR_INN_CHANGE',
      ),
    }
  },
}

const mutations = {
  SET_ITEMS(state, items) {
    state.items = items.map((a) => {
      const currency = currencySymbols[a.currency] ?? a.currency
      return {
        ...a,
        balance: `${a.balance} ${currency}`,
      }
    })
    setTimeout(() => {
      state.isResetSortParams = false
    }, 0)
  },
  SET_ITEM_BY_ID(state, item) {
    state.item = item
  },
  SET_PAGINATION(state, data) {
    state.dataPagination = {
      pageNumber: data.number + 1,
      totalPages: data.totalPages,
    }
  },
  SET_PARAMS(state, params) {
    state.params = {
      ...state.params,
      ...params,
    }
  },
  DELETE_PARAM(state, param) {
    delete state.params[param.name]
  },
  SET_PAGE_NUMBER(state, {page = DEFAULT_PARAMS.page}) {
    state.params = {
      ...state.params,
      page: page - 1,
    }
  },
  SET_ROW_SIZE(state, size = DEFAULT_SIZE) {
    state.params = {
      ...state.params,
      size,
    }
  },
  SET_SORT_PARAMS(state, {sortNameParam, sortSetParam}) {
    const sortStr = sortNameParam && sortSetParam ? `${sortNameParam},${sortSetParam}` : DEFAULT_PARAMS.sort
    state.params = {
      ...state.params,
      sort: sortStr,
    }
  },
  SET_CLIENTS(state, items) {
    state.userIdItems = convertObjToArray(items)
  },
  SET_USER_NAMES(state, {items, withId = true}) {
    state.userNameItems = Object.keys(items)
      .sort((a, b) => b - a)
      .map((key) => ({
        text: withId ? `${key} - ${items[key]}` : items[key],
        value: +key,
      }))
  },
  SET_LOADING(state, {type, loading}) {
    switch (type) {
      case TYPE_LOADING_USER:
        state.loading = loading
        break
      case TYPE_LOADING_USER_ACTION:
        state.actionLoading = loading
        break
    }
  },
  RESET_PARAMS(state) {
    const size = state.params.size
    state.params = {...DEFAULT_PARAMS, size, page: 0}
    state.isShowFilterBar = false
    state.selectedAppFilter = {}
    state.selectedTableFilters = []
    state.isResetSortParams = true
  },
  SET_APP_FILTER(state, {data = {}, isApi = false}) {
    state.isShowFilterBar = true

    if (isApi) {
      state.selectedAppFilter = data
    } else {
      const {value, placeholder, filterType, itemValue} = data
      state.selectedAppFilter = {
        name: value,
        value: '',
        placeholder: placeholder ?? value,
        type: filterType,
        itemValue,
      }
    }
  },
  RESET_TABLE_FILTERS(state) {
    state.selectedTableFilters = []
  },
  SET_TABLE_FILTERS(state, data = undefined) {
    const {name} = state.selectedAppFilter
    const filtersToUpdate = data ?? state.selectedTableFilters

    if (!isDuplicateFilter(state.selectedTableFilters, name)) {
      addFilter(state, filtersToUpdate, data)
    } else {
      updateFilter(filtersToUpdate, state.selectedAppFilter)
    }

    const params = createParams(state.selectedTableFilters)

    this.commit('client/SET_PARAMS', params)
    this.commit('client/SET_PAGE_NUMBER', {page: DEFAULT_PARAMS.page})
  },
  DELETE_TABLE_FILTERS(state, data) {
    state.selectedTableFilters = state.selectedTableFilters.filter((a) => a.name !== data.name)
    this.commit('client/DELETE_PARAM', data)
    this.commit('client/SET_PAGE_NUMBER', {page: DEFAULT_PARAMS.page})

    if (state.selectedTableFilters.length === 0) {
      state.isShowFilterBar = false
    } else {
      state.selectedAppFilter = state.selectedTableFilters[0]
    }
  },
}

const actions = {
  async GET_ITEMS({commit, dispatch}, params) {
    commit('SET_LOADING', {type: TYPE_LOADING_USER, loading: true})
    commit('SET_ITEMS', [])
    commit('SET_ITEM_BY_ID', null)
    await getItems(commit, dispatch, TYPE_LOADING_USER, 'user/client/all', params)
  },
  async GET_ITEM_BY_ID({commit, dispatch}, id) {
    await getItemById(commit, dispatch, TYPE_LOADING_USER, `user/client/view/${id}`)
  },
  async CREATE_USER({commit, dispatch}, formData) {
    return await createItem(
      commit,
      dispatch,
      TYPE_LOADING_USER_ACTION,
      'user/create',
      formData,
      'toast.successUserCreate',
    )
  },
  async EDIT_USER({commit, dispatch}, formData) {
    return await editItem(
      commit,
      dispatch,
      TYPE_LOADING_USER_ACTION,
      `user/client/update/${formData.id}`,
      formData,
      'toast.successUserEdit',
    )
  },
  async ARCHIVED_USER({commit, dispatch}, id) {
    return await deleteItem(
      commit,
      dispatch,
      TYPE_LOADING_USER_ACTION,
      `user/archive/${id}`,
      'toast.successArchive',
    )
  },
  async GET_CLIENTS({commit, dispatch}) {
    commit('SET_LOADING', {type: TYPE_LOADING_USER, loading: true})
    try {
      const res = await axios.get('dictionary/clients/read-all')
      if (res.status === 200) {
        commit('SET_CLIENTS', res.data)
      }
    } catch (err) {
      await customErrors(err, dispatch)
      commit('SET_CLIENTS', [])
    } finally {
      commit('SET_LOADING', {type: TYPE_LOADING_USER, loading: false})
    }
  },
  async GET_USER_NAMES({commit, dispatch}, withId = true) {
    commit('SET_LOADING', {type: TYPE_LOADING_USER, loading: true})
    try {
      const res = await axios.get('dictionary/filter/clients/read-all')
      if (res.status === 200) {
        commit('SET_USER_NAMES', {items: res.data, withId})
      }
    } catch (err) {
      await customErrors(err, dispatch)
      commit('SET_USER_NAMES', [])
    } finally {
      commit('SET_LOADING', {type: TYPE_LOADING_USER, loading: false})
    }
  },
  async GET_CURRENCY({commit, dispatch}, id) {
    try {
      const res = await axios.get(`dictionary/clients/${id}/currency`)
      if (res.status === 200) {
        const {data} = res
        return currencySymbols[data]
      }
    } catch (err) {
      await customErrors(err, dispatch)
      return null
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
